import { IconButton, makeStyles } from '@material-ui/core';
import { type Colors, getBorderColorWithOpacity, isNilOrEmpty } from '@rentguru/commons-utils';
import type { CSSProperties, ReactNode } from 'react';

import { CloseIcon } from '../../icons';

import { Box } from './Box';
import { ConditionalToolTip } from './ConditionalToolTip';

const ICON_SIZE = 12;
const REMOVE_ICON_SIZE = 10;

interface TagChipProps {
  color: Colors | string;
  label: string;
  Icon?: ReactNode;
  className?: string;
  style?: CSSProperties;
  onRemove?: () => void;
  tooltip?: string;
}

const useStyles = makeStyles({
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.4px',
    padding: '2px 8px',
    borderRadius: 10,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    lineHeight: '14px',
  },
  icon: {
    display: 'inline-flex',
    '& svg': { width: ICON_SIZE, height: ICON_SIZE, display: 'block' },
    '& path, & rect, & use': { fill: 'currentColor' },
    '& [fill="none"]': { fill: 'none', stroke: 'currentColor' },
  },
  removeButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    color: 'inherit',
    '&:hover': { backgroundColor: 'transparent' },
    '& svg': { width: REMOVE_ICON_SIZE, height: REMOVE_ICON_SIZE, display: 'block' },
    '& path, & rect, & use': { fill: 'currentColor' },
  },
});

export const TagChip = ({ color, label, Icon, className, style, onRemove, tooltip }: TagChipProps) => {
  const classes = useStyles();
  const chip = (
    <Box
      component="span"
      className={className ? `${classes.tag} ${className}` : classes.tag}
      style={{
        color,
        backgroundColor: getBorderColorWithOpacity(color, 0.1),
        border: `1px solid ${getBorderColorWithOpacity(color, 0.2)}`,
        ...style,
      }}
    >
      {Icon && (
        <Box component="span" className={classes.icon}>
          {Icon}
        </Box>
      )}
      {label}
      {onRemove && (
        <IconButton className={classes.removeButton} onClick={onRemove} disableRipple>
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
  return (
    <ConditionalToolTip isVisible={!isNilOrEmpty(tooltip)} title={tooltip ?? ''} placement="top">
      {chip}
    </ConditionalToolTip>
  );
};
